import { useState } from "react";

/**
 * Hook para manejar formularios.
 * 
 * @param {object} initialState - The initial state of the form.
 * @param {object} validations - An object with field names as keys and objects as values. The objects must have a
 *   `test` property with a function that takes the value of the field as an argument and returns a boolean indicating
 *   whether the value is valid or not, and a `message` property with the error message to display if the value is
 *   invalid.
 * @returns {object} - An object with the following properties:
 *   - `formData`: The current state of the form.
 *   - `errors`: An object with the same keys as `validations` and the error messages as values.
 *   - `handleChange`: A function to call when a form field changes, it takes the event as an argument.
 *   - `handleSubmit`: A function to call when the form is submitted, it takes the event and a callback as arguments.
 */
const useForm = (initialState, validations) => {
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});

/**
 * Valida un campo de formulario según su nombre y valor.
 * 
 * @param {string} name - The name of the form field to validate.
 * @param {string} value - The current value of the form field.
 * @returns {string} - An error message if the value is invalid, otherwise an empty string.
 */

  const validateField = (name, value) => {
    if (validations[name]) {
      return validations[name].test(value) ? "" : validations[name].message;
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const error = validateField(name, value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));

    if (!error) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e, callback) => {
    e.preventDefault();

    const validationErrors = {};
    let isValid = true;

    for (const field in validations) {
      const error = validateField(field, formData[field] || "");
      if (error) {
        isValid = false;
        validationErrors[field] = error;
      }
    }

    setErrors(validationErrors);
    
    if (isValid && callback) {
      callback(formData);
    }
  };

  const resetForm = () => {
    setFormData(initialState);
    setErrors({});
  };

  return {
    formData,
    errors,
    handleChange,
    handleSubmit,
    setFormData,
    resetForm
  };
};

export default useForm;
